import type {AriaAttributes} from 'react';
import React from 'react';

import {FormFieldMetadataContext} from '../FormField';
import {FormFieldGroupContext} from '../FormFieldGroup';
import type {ControlProps, FormFieldElements} from '../FormField';

export type useFieldContextsType = (
  args: Partial<
    ControlProps &
      AriaAttributes & {
        invalid: boolean;
        id: HTMLElement['id'];
        disabled: HTMLInputElement['disabled'];
      }
  >,
) => {
  id?: HTMLElement['id'];
  invalid?: boolean;
  ariaLabel?: AriaAttributes['aria-label'];
  ariaDescribedBy?: AriaAttributes['aria-describedby'];
  ariaLabelledBy?: AriaAttributes['aria-labelledby'];
  disabled?: boolean;
};

export const useFieldContexts: useFieldContextsType = (props) => {
  const {controlId, elements: formFieldElements} = React.useContext(
    FormFieldMetadataContext,
  );
  const {disabled, elements: formFieldGroupElements} = React.useContext(
    FormFieldGroupContext,
  );

  const DESCRIPTION_ELEMENTS: FormFieldElements[] = ['description', 'error'];

  const ariaDescribedBy: string = [formFieldElements, formFieldGroupElements]
    .reduce(
      (arr, elements) =>
        Object.entries(elements)
          .map(([element, {id, present}]) =>
            DESCRIPTION_ELEMENTS.includes(element as FormFieldElements) &&
            present
              ? id
              : null,
          )
          .concat(arr),
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      [] as any, // TS can't infer that these objects are getting reduced to an array of strings; maybe related to https://github.com/microsoft/TypeScript/issues/25454
    )
    .filter(Boolean)
    .join(' ')
    .trim();

  const ariaLabelledBy: string = formFieldElements.label.present
    ? [formFieldElements, formFieldGroupElements]
        .reduce(
          (arr, elements) =>
            Object.entries(elements)
              .map(([element, {id, present}]) =>
                !DESCRIPTION_ELEMENTS.includes(element as FormFieldElements) &&
                present
                  ? id
                  : null,
              )
              .concat(arr),
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          [] as any, // TS can't infer that these objects are getting reduced to an array of strings; maybe related to https://github.com/microsoft/TypeScript/issues/25454
        )
        .filter(Boolean)
        .join(' ')
        .trim()
    : '';

  return {
    // props should win out here for greater user control over validation and flow
    invalid: props.invalid ?? formFieldElements.error.present,
    disabled: props.disabled ?? disabled ?? false,
    ariaLabel: props['aria-label'] ?? undefined,
    // if the user opted out of auto-labeling, leave aria-labelledby alone; otherwise, generate a concatenated string
    ariaLabelledBy:
      ariaLabelledBy.length > 0
        ? ariaLabelledBy
        : props['aria-labelledby'] ?? undefined,
    // FormField context should override props for all other fields to guarantee accessibility
    id: controlId ?? props.id ?? undefined,
    ariaDescribedBy:
      ariaDescribedBy.length > 0
        ? ariaDescribedBy
        : props['aria-describedby'] ?? undefined,
  };
};
